import React from 'react'
import { IconTimes } from '../icons/library'
import { Button } from './simple/button'
import * as RadixPopover from '@radix-ui/react-popover'

export type Step = {
  /** The title of the step */
  title: React.ReactNode
  /** The body of the step */
  body: React.ReactNode
  /** The target element to highlight. This should be a CSS selector. */
  target?: string
  /** Whether to move to the next step when the target is clicked. */
  clickTargetToAdvance?: boolean
  /** Whether to hide the next button. */
  hideNextButton?: boolean
  /** A callback function to run when changing to a new step. */
  onBefore?: () => void
  /** Which side of the target to show the tour panel on. */
  side?: 'top' | 'right' | 'bottom' | 'left'
}

type TourState = {
  open: boolean
  stepIndex: number
}

interface TourProps {
  state: TourState
  setState: React.Dispatch<React.SetStateAction<TourState>>
  steps: Step[]
}

/**
 * A component that displays a tour guide for a web application.
 * It highlights specific elements on the page and provides information about them.
 * @param steps - The steps of the tour, each containing a title, body, and target element.
 * @param state - The current state of the tour, including whether it's open and the current step index.
 * @param setState - A function to update the state of the tour.
 */
export const Tour: React.FC<TourProps> = ({ steps, state, setState }) => {
  const [targetPollingAttempts, setTargetPollingAttempts] = React.useState(0)

  const currentStep = steps[state.stepIndex]
  const isLastStep = state.stepIndex === steps.length - 1
  React.useEffect(() => {
    if (currentStep.onBefore) currentStep.onBefore()
  }, [currentStep])

  const target = currentStep.target !== undefined ? document.querySelector(currentStep.target) : undefined

  const [targetRect, setTargetRect] = React.useState<DOMRect | undefined>(undefined)

  React.useEffect(() => {
    if (!target) {
      if (targetRect) setTargetRect(undefined)
      if (targetPollingAttempts < 100)
        setTimeout(() => {
          setTargetPollingAttempts(targetPollingAttempts + 1)
        }, 100)
      return
    }

    const updateTargetRect = () => {
      setTargetRect(target.getBoundingClientRect())
    }
    const resizeObserver = new ResizeObserver(() => {
      updateTargetRect()
    })
    resizeObserver.observe(target)
    const intervalId = setInterval(() => {
      updateTargetRect()
    }, 100)
    updateTargetRect()
    return () => {
      clearInterval(intervalId)
      resizeObserver.disconnect()
    }
  }, [target, targetPollingAttempts])

  const virtualRef = React.useRef<HTMLElement | null>(null)
  virtualRef.current = target as HTMLElement

  if (!state.open || (!targetRect && currentStep.target)) return null

  return (
    <RadixPopover.Root open={true} onOpenChange={() => {}}>
      <div
        className="fixed inset-0 z-[99998] bg-black/60"
        style={{
          pointerEvents: 'all',
          mixBlendMode: 'hard-light',
        }}
        onPointerDown={(e) => {
          e.stopPropagation()
        }}
      >
        {targetRect && (
          <div
            className={`absolute border-2 border-gray-700 ${currentStep.clickTargetToAdvance ? 'cursor-pointer' : ''}`}
            onClick={() => {
              if (currentStep.clickTargetToAdvance) {
                target!.dispatchEvent(new MouseEvent('click', { bubbles: true }))
                // now click body, to close any open popovers
                document.body.dispatchEvent(new MouseEvent('pointerdown', { bubbles: true }))
                setState({ open: true, stepIndex: state.stepIndex + 1 })
              }
            }}
            style={{
              background: '#808080',
              top: targetRect.top - 4,
              left: targetRect.left - 4,
              width: targetRect.width + 8,
              height: targetRect.height + 8,
              borderRadius: 8,
            }}
          ></div>
        )}
      </div>
      {target ? (
        <RadixPopover.Anchor virtualRef={virtualRef} />
      ) : (
        <RadixPopover.Anchor asChild>
          <div className="fixed inset-x-1/2 top-1/3 h-0 w-0"></div>
        </RadixPopover.Anchor>
      )}
      <RadixPopover.Portal>
        <RadixPopover.Content
          side={currentStep.side}
          collisionPadding={8}
          sideOffset={10}
          className="!pointer-events-auto relative z-[99999] flex flex-col gap-4 rounded-2xl bg-white p-4 shadow-lg"
          style={{ maxWidth: 'min(calc(100vw - 2rem), 425px, var(--radix-popover-content-available-width))' }}
          onPointerDown={(e) => {
            e.stopPropagation()
          }}
        >
          <Button
            close
            className="absolute right-1 top-1"
            onClick={() => {
              setState({ open: false, stepIndex: 0 })
            }}
          >
            <IconTimes />
          </Button>
          <div className="text-2xl font-bold">{currentStep.title}</div>
          <div className="leading-5">{currentStep.body}</div>
          {!currentStep.clickTargetToAdvance && !currentStep.hideNextButton && (
            <Button
              logo
              className="self-end"
              onClick={() => {
                if (isLastStep) {
                  setState({ open: false, stepIndex: 0 })
                } else {
                  setState({ open: true, stepIndex: state.stepIndex + 1 })
                }
              }}
            >
              {isLastStep ? 'Close' : 'Next'}
            </Button>
          )}
          {target && <RadixPopover.Arrow width={12} height={6} className="fill-white" />}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  )
}
